import { message } from "antd"
import {
  withPointBalanceRefresh,
  runPointAction,
} from "~/common/billing/pointAction"
import { getModelFigureArtlinePointCost } from "~/common/billing/pointCosts"
import { reconcileImageMentionLabels } from "~/common/media/resourceMentions"
import {
  isImageTaskFailedStatus,
  isImageTaskProcessingStatus,
  isImageTaskSucceededStatus,
} from "~/common/constants"
import { pollHotVideoTask } from "~/common/tasks/hotVideoTask"
import {
  getRecreateArtlineInputError,
  getRecreateModelArtline,
  getRecreateGenerationResources,
  type RecreateModel,
} from "~/common/video/recreate"
import { createHotVideoModelFigureArtline } from "~/api/task"

import type { HotVideoState, HotVideoSetState, HotVideoGetState } from "./types"
import {
  getCustomModel,
  artlineRequestGuard,
  promptSaver,
  getPromptState,
} from "./shared"

export const createModelArtlineActions = (
  set: HotVideoSetState,
  get: HotVideoGetState,
): Pick<HotVideoState, "generateModelFigureArtline"> => ({
  generateModelFigureArtline: async (options = {}) => {
    const state = get()
    if (
      state.resourcesPending ||
      state.isGeneratingModelArtline ||
      state.isRequesting ||
      state.isRestoringTaskDraft ||
      state.isSubmittingVideoTask
    )
      return false
    const inputError = getRecreateArtlineInputError(state)
    if (inputError) {
      void message.warning(inputError)
      return false
    }
    const { draftRevision, referenceModel } = state
    let taskId = state.taskId
    if (referenceModel.mode !== "custom") return false
    const isStale = artlineRequestGuard.begin(
      () =>
        Boolean(taskId && get().taskId !== taskId) ||
        get().draftRevision !== draftRevision,
    )
    set({ isGeneratingModelArtline: true, modelArtlineError: "" })
    const applyArtline = (artline: API.Task.ModelFigureArtlineInfo) => {
      const current = get()
      const nextModel = { ...referenceModel, artline }
      // 线稿接口会直接更新任务；最终生成需校验这份已持久化的资源。
      set({ savedReferenceModel: nextModel })
      if (current.referenceModel.mode !== "custom") {
        set({ modelDrafts: { ...current.modelDrafts, custom: nextModel } })
        return
      }
      const imageUrls = (model: RecreateModel) =>
        getRecreateGenerationResources({
          ...current,
          savedReferenceModel: model,
        })
          .filter((resource) => resource.type?.startsWith("image/"))
          .map((resource) => resource.url)
      const prompt = reconcileImageMentionLabels(
        current.replicaPrompt,
        imageUrls(current.savedReferenceModel ?? current.referenceModel),
        imageUrls(nextModel),
        { preserveReplacedPositions: true },
      ).value
      set({
        referenceModel: nextModel,
        ...(prompt !== current.replicaPrompt ? getPromptState(prompt) : {}),
      })
      if (prompt !== current.replicaPrompt) {
        promptSaver.update(get().replicaPrompt)
        void promptSaver.flush()
      }
    }
    try {
      let artline = getRecreateModelArtline(referenceModel)
      const processing = Boolean(
        artline && isImageTaskProcessingStatus(artline.status),
      )
      if (!options.resume && !processing) {
        const response = await runPointAction(
          getModelFigureArtlinePointCost(),
          async () => {
            const currentTaskId = await get().ensureTaskId()
            if (isStale() || !currentTaskId || get().resourcesPending)
              return undefined
            taskId = currentTaskId
            return createHotVideoModelFigureArtline({
              task_id: taskId,
              image_urls: [referenceModel.url],
            })
          },
          { isStale: () => isStale() || get().resourcesPending },
        )
        if (isStale() || !response) return false
        artline = response.data?.model_figure_artline || null
        if (!artline) throw new Error("未获取到模特线稿任务，请重试")
        applyArtline(artline)
      } else {
        const currentTaskId = await get().ensureTaskId()
        if (isStale() || !currentTaskId) return false
        taskId = currentTaskId
      }
      if (!artline || isImageTaskProcessingStatus(artline.status)) {
        const result = await withPointBalanceRefresh(() =>
          pollHotVideoTask({
            taskId,
            isStale,
            onTask: (task) => {
              if (task.model_figure_artline)
                applyArtline(task.model_figure_artline)
            },
            isDone: (task) =>
              Boolean(
                task.model_figure_artline &&
                isImageTaskSucceededStatus(task.model_figure_artline.status),
              ),
            isFailed: (task) =>
              Boolean(
                task.model_figure_artline &&
                isImageTaskFailedStatus(task.model_figure_artline.status),
              ),
            getErrorMessage: (task) =>
              task.model_figure_artline?.error_message ||
              "模特线稿图生成失败，请重试",
            timeoutMessage: "模特线稿图生成超时，请重试",
          }),
        )
        if (isStale()) return false
        artline = result?.model_figure_artline || null
      }
      if (
        !artline ||
        !isImageTaskSucceededStatus(artline.status) ||
        !artline.result_urls.length
      )
        throw new Error(
          artline?.error_message || "未生成有效的模特线稿图，请重试",
        )
      applyArtline(artline)
      const customModel = getCustomModel(get())
      if (!customModel || !getRecreateModelArtline(customModel))
        throw new Error("模特图片已变更，请重新生成线稿图")
      return true
    } catch (error) {
      if (!isStale())
        set({
          modelArtlineError:
            error instanceof Error
              ? error.message
              : "模特线稿图生成失败，请重试",
        })
      return false
    } finally {
      if (!isStale()) set({ isGeneratingModelArtline: false })
    }
  },
})
